import { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Search, Trash2 } from 'lucide-react';
import {
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  mergeAttributes,
  type JSONContent,
  type NodeViewProps,
} from '@tiptap/react';

import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Page provider — the editor is generic, so each call site (standalone Wiki,
// project Pages) injects how to search/resolve/open its own pages. Kept tiny so
// the provider never leaks a page the API wouldn't already return.
// ---------------------------------------------------------------------------
export interface EditorPageRef {
  id: number;
  public_id: string;
  title: string;
  icon?: string;
}
export interface EditorPageContent extends EditorPageRef {
  body: string;
  body_json?: JSONContent | null;
}
export interface EditorPageSubtreeNode extends EditorPageContent {
  depth: number;
}
export interface PageProvider {
  /** Search pages by text — lightweight refs for the picker. */
  search(query: string): Promise<EditorPageRef[]>;
  /** Resolve one page's content for inline rendering. */
  resolve(publicId: string): Promise<EditorPageContent>;
  /** A page + its descendant subtree (depth-capped, permission-filtered server-side). */
  subtree(publicId: string): Promise<{ pages: EditorPageSubtreeNode[]; truncated: boolean }>;
  /** Open/navigate to a page (link / list-item click). */
  open(publicId: string): void;
  /** The page being edited — a "/child pages" block defaults to listing its
   *  children (Confluence "Children Display"), so no target needs picking. */
  hostPageId?: string | null;
}

// link = clean inline link · content = transclude this page's body ·
// children = Confluence-style nested list of child-page links.
export type PageEmbedMode = 'link' | 'content' | 'children';

interface PageEmbedOptions {
  provider?: PageProvider;
  /** Render the read-only document of a stored page (set by BlockEditor). */
  renderContent?: (page: EditorPageContent) => React.ReactNode;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    pageEmbed: { setPageEmbed: (attrs?: { mode?: PageEmbedMode }) => ReturnType };
  }
}

// ---------------------------------------------------------------------------
// Node
// ---------------------------------------------------------------------------
export const PageEmbed = Node.create<PageEmbedOptions>({
  name: 'pageEmbed',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    return { provider: undefined, renderContent: undefined };
  },

  addAttributes() {
    return {
      pageId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-page-id'),
        renderHTML: (attrs) => (attrs.pageId ? { 'data-page-id': attrs.pageId } : {}),
      },
      mode: {
        default: 'link' as PageEmbedMode,
        parseHTML: (el) => el.getAttribute('data-page-mode') || 'link',
        renderHTML: (attrs) => ({ 'data-page-mode': attrs.mode }),
      },
      label: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-page-label') || el.textContent || '',
        renderHTML: (attrs) => (attrs.label ? { 'data-page-label': attrs.label } : {}),
      },
      icon: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-page-icon') || '',
        renderHTML: (attrs) => (attrs.icon ? { 'data-page-icon': attrs.icon } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[data-type="page-embed"]' }];
  },

  // Static HTML mirror (used for the stored `body` + portal/export render): a
  // plain link to the page so the reference survives even without the provider.
  renderHTML({ HTMLAttributes, node }) {
    const label = (node.attrs.label as string) || 'Page';
    const icon = (node.attrs.icon as string) || '';
    return [
      'a',
      mergeAttributes(HTMLAttributes, { 'data-type': 'page-embed', class: 'page-embed-link' }),
      `${icon ? `${icon} ` : ''}${label}`,
    ];
  },

  addCommands() {
    return {
      setPageEmbed:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { mode: attrs?.mode ?? 'link', pageId: null, label: '', icon: '' },
          }),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(PageEmbedView);
  },
});

// ---------------------------------------------------------------------------
// Node view
// ---------------------------------------------------------------------------
function PageEmbedView({ node, updateAttributes, deleteNode, editor, extension }: NodeViewProps) {
  const provider = (extension.options as PageEmbedOptions).provider;
  const renderContent = (extension.options as PageEmbedOptions).renderContent;
  const editable = editor.isEditable;
  const pageId = node.attrs.pageId as string | null;
  const mode = (node.attrs.mode as PageEmbedMode) ?? 'link';
  const label = (node.attrs.label as string) || '';
  const icon = (node.attrs.icon as string) || '';

  // No provider (e.g. content transcluded from another page) → static link so
  // nested embeds never recurse or fetch.
  if (!provider) {
    return (
      <NodeViewWrapper className="my-1 cursor-default" contentEditable={false}>
        <PageLink icon={icon} label={label} />
      </NodeViewWrapper>
    );
  }

  // "Child pages" lists the host page's sub-pages by default (Confluence-style),
  // so it needs no picked target — only link/content do.
  const childrenTarget = pageId ?? provider.hostPageId ?? null;
  if (mode !== 'children' && !pageId) {
    return (
      <NodeViewWrapper className="my-1.5 cursor-default" contentEditable={false}>
        {editable ? (
          <PagePicker
            provider={provider}
            onPick={(p) => updateAttributes({ pageId: p.public_id, label: p.title, icon: p.icon ?? '' })}
            onCancel={deleteNode}
          />
        ) : null}
      </NodeViewWrapper>
    );
  }

  const open = () => {
    const id = pageId ?? provider.hostPageId;
    if (id) provider.open(id);
  };

  return (
    <NodeViewWrapper className="group/embed my-1.5 cursor-default" contentEditable={false}>
      {mode === 'link' && pageId ? (
        <PageLink icon={icon} label={label} onClick={open} />
      ) : mode === 'children' ? (
        childrenTarget ? (
          <ChildrenList key={childrenTarget} provider={provider} pageId={childrenTarget} />
        ) : (
          <p className="text-muted-foreground py-1 text-sm italic">No sub-pages yet.</p>
        )
      ) : pageId ? (
        <ContentEmbed
          key={pageId}
          provider={provider}
          pageId={pageId}
          icon={icon}
          label={label}
          renderContent={renderContent}
          onOpen={open}
        />
      ) : null}
      {editable ? (
        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs opacity-0 transition-opacity group-hover/embed:opacity-100">
          <ModeButton current={mode} value="link" label="Link" onClick={(m) => updateAttributes({ mode: m })} />
          <ModeButton current={mode} value="content" label="Content" onClick={(m) => updateAttributes({ mode: m })} />
          <ModeButton current={mode} value="children" label="Child pages" onClick={(m) => updateAttributes({ mode: m })} />
          <button
            type="button"
            className="hover:text-destructive ml-1 inline-flex items-center gap-1"
            onClick={() => deleteNode()}
          >
            <Trash2 className="size-3" /> Remove
          </button>
        </div>
      ) : null}
    </NodeViewWrapper>
  );
}

// ---------------------------------------------------------------------------
// Pieces — clean / borderless (Confluence-like)
// ---------------------------------------------------------------------------
/** A single page reference rendered as a clean underlined link with a doc icon. */
function PageLink({
  icon,
  label,
  onClick,
  className,
}: {
  icon?: string;
  label: string;
  onClick?: () => void;
  className?: string;
}) {
  const content = (
    <>
      {icon ? (
        <span className="text-base leading-none">{icon}</span>
      ) : (
        <FileText className="text-muted-foreground size-4 shrink-0" />
      )}
      <span className="truncate underline decoration-muted-foreground/40 underline-offset-2">
        {label || 'Untitled page'}
      </span>
    </>
  );
  if (!onClick) {
    return <span className={cn('inline-flex items-center gap-1.5 text-sm', className)}>{content}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'hover:text-primary inline-flex cursor-pointer items-center gap-1.5 text-left text-sm font-medium',
        className,
      )}
    >
      {content}
    </button>
  );
}

function ModeButton({
  current,
  value,
  label,
  onClick,
}: {
  current: PageEmbedMode;
  value: PageEmbedMode;
  label: string;
  onClick: (m: PageEmbedMode) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn('rounded px-1.5 py-0.5', current === value ? 'bg-muted text-foreground' : 'hover:bg-muted')}
    >
      {label}
    </button>
  );
}

function PagePicker({
  provider,
  onPick,
  onCancel,
}: {
  provider: PageProvider;
  onPick: (p: EditorPageRef) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EditorPageRef[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clicking outside the picker cancels the embed (removes the empty node) —
  // the user changed their mind. Esc does the same via the input handler.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as globalThis.Node)) {
        onCancel();
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onCancel]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      provider
        .search(query)
        .then((rows) => !cancelled && setResults(rows))
        .catch(() => !cancelled && setResults([]))
        .finally(() => !cancelled && setLoading(false));
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, provider]);

  return (
    <div ref={containerRef} className="bg-card rounded-md border p-2 shadow-sm">
      <div className="flex items-center gap-2 border-b px-1 pb-2">
        <Search className="text-muted-foreground size-4" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onCancel()}
          placeholder="Search pages to embed…"
          className="placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
        />
        {loading ? <Loader2 className="text-muted-foreground size-4 animate-spin" /> : null}
      </div>
      <ul className="max-h-56 overflow-y-auto pt-1">
        {results.length === 0 && !loading ? (
          <li className="text-muted-foreground px-2 py-1.5 text-sm">No pages found</li>
        ) : (
          results.map((p) => (
            <li key={p.public_id}>
              <button
                type="button"
                onClick={() => onPick(p)}
                className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
              >
                <span className="text-base leading-none">{p.icon || '📄'}</span>
                <span className="min-w-0 flex-1 truncate">{p.title || 'Untitled page'}</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/** Hook: resolve a page's content once (keyed remount resets state per page). */
function usePageContent(provider: PageProvider, pageId: string) {
  const [page, setPage] = useState<EditorPageContent | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    let cancelled = false;
    provider
      .resolve(pageId)
      .then((p) => !cancelled && (setPage(p), setState('ready')))
      .catch(() => !cancelled && setState('error'));
    return () => {
      cancelled = true;
    };
  }, [provider, pageId]);
  return { page, state };
}

/** Transclude one page's body inline, flush with the host document. */
function ContentEmbed({
  provider,
  pageId,
  icon,
  label,
  renderContent,
  onOpen,
}: {
  provider: PageProvider;
  pageId: string;
  icon: string;
  label: string;
  renderContent?: (page: EditorPageContent) => React.ReactNode;
  onOpen: () => void;
}) {
  const { page, state } = usePageContent(provider, pageId);
  return (
    <div className="border-muted border-l-2 pl-3">
      <PageLink icon={icon} label={label} onClick={onOpen} className="text-muted-foreground mb-1" />
      {state === 'loading' ? (
        <Loading />
      ) : state === 'error' || !page ? (
        <Unavailable />
      ) : renderContent ? (
        renderContent(page)
      ) : (
        <p className="text-muted-foreground text-sm">{page.title || 'Untitled page'}</p>
      )}
    </div>
  );
}

/** Confluence "Children Display" — a clean nested list of child-page links. */
function ChildrenList({ provider, pageId }: { provider: PageProvider; pageId: string }) {
  const [nodes, setNodes] = useState<EditorPageSubtreeNode[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    provider
      .subtree(pageId)
      .then((r) => {
        if (cancelled) return;
        setNodes(r.pages);
        setTruncated(r.truncated);
        setState('ready');
      })
      .catch(() => !cancelled && setState('error'));
    return () => {
      cancelled = true;
    };
  }, [provider, pageId]);

  if (state === 'loading') return <Loading />;
  if (state === 'error') return <Unavailable />;

  // Drop the root (depth 0 = the page hosting the block); show its descendants.
  const children = (nodes ?? []).filter((p) => p.depth > 0);
  if (children.length === 0) {
    return <p className="text-muted-foreground py-1 text-sm italic">No sub-pages yet.</p>;
  }
  return (
    <div className="space-y-1.5 py-1">
      {children.map((p) => (
        <div key={p.public_id} style={{ marginLeft: (p.depth - 1) * 18 }}>
          <PageLink icon={p.icon} label={p.title} onClick={() => provider.open(p.public_id)} />
        </div>
      ))}
      {truncated ? (
        <p className="text-muted-foreground text-xs italic">Showing the first pages — open the page to see the rest.</p>
      ) : null}
    </div>
  );
}

function Loading() {
  return (
    <div className="text-muted-foreground flex items-center gap-2 py-1 text-sm">
      <Loader2 className="size-4 animate-spin" /> Loading…
    </div>
  );
}

function Unavailable() {
  return <p className="text-muted-foreground py-1 text-sm">This page is unavailable.</p>;
}
